import { Fragment, Suspense, lazy, useMemo, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  GitBranch,
  HelpCircle,
  Loader2,
  Pencil,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useEditSnippet,
  usePromoteRegionHeader,
  useSetHeaderNotApplicable,
  useDeleteScope,
  useResolvedNodeAnswers,
} from '@/hooks/useKnowledgeBase';
import type {
  CategoryCount,
  ResolvedCategory,
  ResolvedNode,
  ResolvedQuestionOut,
  ResolvedSnippetOut,
  ResolvedStatus,
  ResolvedStoryResponse,
} from '../../../../api/kbResolvedStory';
import { ContentBlockAuthoring } from './ContentBlockAuthoring';
import { RichText } from './RichText';

// Lazy: the TipTap editor bundle loads only when a snippet is actually edited.
const RichTextEditor = lazy(() =>
  import('./RichTextEditor').then((m) => ({ default: m.RichTextEditor }))
);

/** #2 authoring UI is opt-in: set VITE_CONTENT_BLOCKS="true" to expose the write controls. */
const CONTENT_BLOCKS_ENABLED = import.meta.env.VITE_CONTENT_BLOCKS === 'true';

export interface ResolvedStoryViewProps {
  /** Resolved story (caller owns the query via useResolvedStory). */
  story?: ResolvedStoryResponse;
  /** True while the story is loading. */
  isLoading?: boolean;
  className?: string;
}

/** A node with its resolved children, rebuilt from the flat node list. */
interface ResolvedTreeNode extends ResolvedNode {
  children: ResolvedTreeNode[];
}

/** Left-tree selection: a spine node, or a category within it. */
interface Selection {
  nodeId: number;
  catIndex: number | null;
}

/** Heading size by hierarchy depth — top nodes largest, nested smaller. */
function headingClass(depth: number): string {
  if (depth <= 0) return 'text-lg font-semibold';
  if (depth === 1) return 'text-base font-semibold';
  return 'text-sm font-medium';
}

/**
 * Rebuild the node tree from the FLAT `nodes` list (ordered by depth then
 * node_id). Parents always precede their children, so a single pass keyed by
 * node_id resolves every parent before its children are attached.
 */
function buildTree(nodes: ResolvedNode[]): ResolvedTreeNode[] {
  const byId = new Map<number, ResolvedTreeNode>();
  const roots: ResolvedTreeNode[] = [];

  for (const node of nodes) {
    byId.set(node.node_id, { ...node, children: [] });
  }
  for (const node of nodes) {
    const treeNode = byId.get(node.node_id)!;
    const parent = node.parent_id !== null ? byId.get(node.parent_id) : undefined;
    if (parent) {
      parent.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }
  return roots;
}

/** Visual treatment for a provenance/status value. */
function statusStyle(status: ResolvedStatus): { label: string; className: string } {
  switch (status) {
    case 'own':
      return { label: 'Own', className: 'border-green-300 bg-green-50 text-green-700' };
    case 'inherited':
      return { label: 'Inherited', className: 'border-blue-300 bg-blue-50 text-blue-700' };
    case 'gap':
      return { label: 'Gap — needs answer', className: 'border-red-300 bg-red-50 text-red-700' };
    case 'not_applicable':
      return { label: 'N/A', className: 'border-gray-300 bg-gray-100 text-gray-600' };
  }
}

/** The question-level status badge (own / inherited+depth / gap / N/A). */
function StatusBadge({ question }: { question: ResolvedQuestionOut }) {
  const { label, className } = statusStyle(question.status);
  const inheritedDepth =
    question.status === 'inherited'
      ? question.snippets
          .filter((s) => s.provenance === 'inherited')
          .reduce<number | null>(
            (min, s) => (min === null ? s.source_depth : Math.min(min, s.source_depth)),
            null
          )
      : null;

  return (
    <Badge variant="outline" className={cn('text-xs py-0 px-1.5', className)}>
      {label}
      {inheritedDepth !== null && (
        <span className="ml-1 opacity-80">from depth {inheritedDepth}</span>
      )}
    </Badge>
  );
}

/** A per-snippet provenance chip (own=green / inherited=blue with source depth). */
function ProvenanceChip({
  provenance,
  sourceDepth,
}: {
  provenance: ResolvedStatus;
  sourceDepth: number;
}) {
  const { label, className } = statusStyle(provenance);
  return (
    <Badge variant="outline" className={cn('text-xs py-0 px-1.5', className)}>
      {label}
      {provenance === 'inherited' && (
        <span className="ml-1 opacity-80">from depth {sourceDepth}</span>
      )}
    </Badge>
  );
}

// Seed the editor from the rich body when present, else wrap the plain text (escaped) in a <p>.
function seedHtml(snippet: ResolvedSnippetOut): string {
  if (snippet.body_html) return snippet.body_html;
  const plain = (snippet.body_text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
  return `<p>${plain}</p>`;
}

function SnippetBody({ snippet }: { snippet: ResolvedSnippetOut }) {
  const [editing, setEditing] = useState(false);
  const [draftHtml, setDraftHtml] = useState(() => seedHtml(snippet));
  const editMutation = useEditSnippet();
  const deleteMutation = useDeleteScope();
  const canEdit = Boolean(snippet.block_key);
  // Un-place only where this node OWNS the placement (inherited snippets live on an ancestor).
  const canUnplace = snippet.provenance === 'own' && Boolean(snippet.scope_id);
  // Empty if the draft has no text content (tags stripped) — gates Save.
  const draftIsEmpty = draftHtml.replace(/<[^>]+>/g, '').trim().length === 0;

  const startEdit = () => {
    setDraftHtml(seedHtml(snippet));
    editMutation.reset();
    setEditing(true);
  };
  const save = () => {
    // Server re-derives the authoritative plain text from the sanitized HTML; we send a
    // best-effort projection to satisfy the required body_text field.
    const plain = draftHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    editMutation.mutate(
      { blockKey: snippet.block_key, bodyText: plain, bodyHtml: draftHtml },
      { onSuccess: () => setEditing(false) }
    );
  };

  if (editing) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50/40 p-2.5 space-y-2">
        <Suspense
          fallback={<div className="text-xs text-muted-foreground p-2">Loading editor…</div>}
        >
          <RichTextEditor initialHtml={draftHtml} onChange={setDraftHtml} />
        </Suspense>
        {editMutation.isError && (
          <p className="text-xs text-red-600">Couldn’t save the edit — please try again.</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={save} disabled={editMutation.isPending || draftIsEmpty}>
            {editMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(false)}
            disabled={editMutation.isPending}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <span className="text-xs text-muted-foreground">
            Edits the source snippet — propagates to every level that inherits it.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-md border p-2.5">
      {snippet.body_html ? (
        <RichText html={snippet.body_html} />
      ) : (
        <p className="text-sm whitespace-pre-wrap text-foreground">{snippet.body_text}</p>
      )}
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <ProvenanceChip provenance={snippet.provenance} sourceDepth={snippet.source_depth} />
        {snippet.placement_role && (
          <Badge variant="outline" className="text-xs py-0 px-1.5">
            {snippet.placement_role}
          </Badge>
        )}
        {(canEdit || canUnplace) && (
          <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
            {canEdit && (
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={startEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
            )}
            {canUnplace && (
              <Button
                size="sm"
                variant="ghost"
                disabled={deleteMutation.isPending}
                className="h-6 px-2 text-red-600 hover:text-red-700"
                onClick={() => {
                  if (
                    window.confirm(
                      'Remove this placement? It stops inheriting everywhere it currently flows.'
                    )
                  ) {
                    deleteMutation.mutate(snippet.scope_id as string);
                  }
                }}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                )}
                Remove
              </Button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function QuestionRow({ question }: { question: ResolvedQuestionOut }) {
  const showBody = !question.is_gap && question.status !== 'gap';

  return (
    <div className="rounded-md border p-2.5 space-y-2">
      <div className="flex items-start gap-1.5">
        <HelpCircle className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-sm font-medium flex-1">{question.question_text}</p>
        <StatusBadge question={question} />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap pl-5">
        <span className="text-xs text-muted-foreground">{question.composition_mode}</span>
        <span className="text-xs text-muted-foreground">
          · {question.inheritable ? 'inheritable' : 'non-inheritable'}
        </span>
      </div>

      {showBody && question.snippets.length > 0 && (
        <div className="space-y-2 pl-5">
          {question.snippets.map((snippet, i) => (
            <SnippetBody key={`${snippet.block_key}-${i}`} snippet={snippet} />
          ))}
        </div>
      )}

      {question.notes.length > 0 && (
        <div className="flex flex-wrap gap-x-2 pl-5">
          {question.notes.map((note, i) => (
            <span key={i} className="text-xs text-muted-foreground italic">
              {note}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryGroup({ category }: { category: ResolvedCategory }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {category.display_name}
      </p>
      <div className="space-y-2">
        {category.questions.map((q) => (
          <QuestionRow key={q.canonical_question_id} question={q} />
        ))}
      </div>
    </div>
  );
}

// JPM SME product leveling — human labels for the product-axis node kinds.
const KIND_LABELS: Record<string, string> = {
  firm: 'Firm',
  folder: 'Folder',
  asset_class: 'Asset Class',
  region: 'Region',
  strategy: 'Strategy',
  vehicle: 'Vehicle',
  engine_product_area: 'Engine-Product Area',
  investment_product: 'Product',
  product: 'Product',
  share_class_composite: 'Share Class-Composite',
  share_class: 'Share Class',
  composite: 'Composite',
  data_folder: 'Data Folder',
};

// A "loose" strategy hangs directly off its asset class (its name carried no region).
// Legacy spines lifted such funds as node_kind='product'; the SME-locked seed uses
// 'strategy'. Treat both as region-less for the "Not region-specific" divider.
const LOOSE_STRATEGY_KINDS = new Set(['strategy', 'product']);

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Aggregate a node's per-category counts into one status tally. */
function sumCounts(cats: CategoryCount[]): {
  own: number;
  inherited: number;
  gap: number;
  not_applicable: number;
} {
  return cats.reduce(
    (acc, c) => ({
      own: acc.own + c.own,
      inherited: acc.inherited + c.inherited,
      gap: acc.gap + c.gap,
      not_applicable: acc.not_applicable + c.not_applicable,
    }),
    { own: 0, inherited: 0, gap: 0, not_applicable: 0 }
  );
}

/** At-a-glance status dots from structural counts (skeleton). */
function StatusDots({
  counts,
}: {
  counts: { own: number; inherited: number; gap: number; not_applicable: number };
}) {
  const items: Array<[number, string, string]> = [
    [counts.own, 'bg-green-500', 'own'],
    [counts.inherited, 'bg-blue-500', 'inherited'],
    [counts.gap, 'bg-red-400', 'gap'],
    [counts.not_applicable, 'bg-gray-400', 'n/a'],
  ];
  const shown = items.filter(([n]) => n > 0);
  if (shown.length === 0) return null;
  return (
    <span className="flex items-center gap-1.5">
      {shown.map(([n, color, label]) => (
        <span
          key={label}
          className="flex items-center gap-0.5 text-xs text-muted-foreground tabular-nums"
          title={`${n} ${label}`}
        >
          <span className={cn('h-2 w-2 rounded-full', color)} />
          {n}
        </span>
      ))}
    </span>
  );
}

/** A category row injected under a spine node in the LEFT tree. */
function CategoryRow({
  node,
  catIndex,
  depth,
  selected,
  onSelect,
}: {
  node: ResolvedTreeNode;
  catIndex: number;
  depth: number;
  selected: boolean;
  onSelect: (sel: Selection) => void;
}) {
  const cat = node.category_counts[catIndex];
  return (
    <button
      type="button"
      onClick={() => onSelect({ nodeId: node.node_id, catIndex })}
      style={{ paddingLeft: depth * 12 + 28 }}
      className={cn(
        'flex items-center gap-2 w-full text-left py-1 pr-1.5 rounded-md transition-colors',
        selected ? 'bg-primary/10' : 'hover:bg-muted/60'
      )}
    >
      <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className={cn('truncate text-sm', selected ? 'font-medium text-foreground' : 'text-muted-foreground')}>
        {cat.display_name}
      </span>
      <span className="ml-auto shrink-0">
        <StatusDots counts={cat} />
      </span>
    </button>
  );
}

/** A spine node in the LEFT tree: chevron toggles children, the label selects it,
 *  and its categories are injected as child rows beneath it. */
/** Enrichment actions on a level-header: "Model" (lazy-promote) + "N/A" (opt-out), or undo. */
function HeaderActions({ node }: { node: ResolvedTreeNode }) {
  const setNA = useSetHeaderNotApplicable();
  const promote = usePromoteRegionHeader();
  const busy = setNA.isPending || promote.isPending;
  const canModel = node.enrichable_count != null && node.enrichable_total != null;

  if (node.not_applicable) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => setNA.mutate({ nodeId: node.node_id, notApplicable: false })}
        className="text-[10px] text-muted-foreground hover:text-foreground hover:underline shrink-0 disabled:opacity-50"
      >
        undo
      </button>
    );
  }
  if (!node.not_modeled) return null;
  return (
    <span className="flex items-center gap-2 shrink-0">
      {canModel && (
        <button
          type="button"
          disabled={busy}
          onClick={() => promote.mutate(node.node_id)}
          className="text-[10px] text-amber-600 hover:underline disabled:opacity-50"
        >
          {promote.isPending ? 'Modeling…' : 'Model'}
        </button>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => setNA.mutate({ nodeId: node.node_id, notApplicable: true })}
        className="text-[10px] text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
        title="Not applicable to this firm"
      >
        N/A
      </button>
    </span>
  );
}

function TreeNode({
  node,
  depth,
  selection,
  onSelect,
}: {
  node: ResolvedTreeNode;
  depth: number;
  selection: Selection | null;
  onSelect: (sel: Selection) => void;
}) {
  const [open, setOpen] = useState(depth <= 1);
  const isHeader = node.node_kind === 'level_header';
  const hasCount = node.enrichable_count != null && node.enrichable_total != null;
  const hasCategories = node.category_counts.length > 0;
  const hasChildren = node.children.length > 0;
  const expandable = hasCategories || hasChildren;
  const nodeSelected = selection?.nodeId === node.node_id && selection.catIndex === null;
  const Chevron = open ? ChevronDown : ChevronRight;
  const nodeCounts = sumCounts(node.category_counts);

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 rounded-md pr-1.5 transition-colors',
          nodeSelected ? 'bg-primary/10' : 'hover:bg-muted/60'
        )}
        style={{ paddingLeft: depth * 12 }}
      >
        {expandable ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="p-1 text-muted-foreground hover:text-foreground shrink-0"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            <Chevron className="h-4 w-4" />
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        {isHeader ? (
          <div
            className="flex items-center gap-2 flex-1 min-w-0 py-1.5"
            title={node.blurb ?? undefined}
          >
            <button
              type="button"
              onClick={() => onSelect({ nodeId: node.node_id, catIndex: null })}
              className={cn(
                'truncate text-sm italic text-left min-w-0',
                node.not_applicable ? 'text-muted-foreground/50' : 'text-muted-foreground',
                nodeSelected ? 'font-semibold' : 'font-medium'
              )}
            >
              {node.display_name}
            </button>
            {node.not_applicable ? (
              <Badge
                variant="outline"
                className="text-[10px] py-0 px-1 font-normal shrink-0 text-muted-foreground"
              >
                not applicable
              </Badge>
            ) : node.not_modeled ? (
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px] py-0 px-1 font-normal shrink-0 border-dashed',
                  hasCount ? 'border-amber-400 text-amber-600' : ''
                )}
              >
                {hasCount
                  ? `${node.enrichable_count} of ${node.enrichable_total} ready`
                  : 'not modeled'}
              </Badge>
            ) : null}
            <HeaderActions node={node} />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onSelect({ nodeId: node.node_id, catIndex: null })}
            className="flex items-center gap-2 flex-1 min-w-0 text-left py-1.5"
          >
            <span
              className={cn('truncate text-sm', nodeSelected ? 'font-semibold' : 'font-medium')}
            >
              {node.display_name}
            </span>
            <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal shrink-0">
              {kindLabel(node.node_kind)}
            </Badge>
            <span className="ml-auto shrink-0">
              <StatusDots counts={nodeCounts} />
            </span>
          </button>
        )}
      </div>

      {open && expandable && (
        <div className="space-y-0.5">
          {/* categories injected at this level */}
          {node.category_counts.map((cat, i) => (
            <CategoryRow
              key={cat.category_node_id ?? `cat-${i}`}
              node={node}
              catIndex={i}
              depth={depth}
              selected={selection?.nodeId === node.node_id && selection.catIndex === i}
              onSelect={onSelect}
            />
          ))}
          {/* child spine nodes. After a Region is modeled, an asset class's region-less
              products are lifted here — flag them so the structure is self-explanatory. */}
          {node.children.map((child, idx) => {
            const showRegionless =
              node.node_kind === 'asset_class' &&
              LOOSE_STRATEGY_KINDS.has(child.node_kind) &&
              !LOOSE_STRATEGY_KINDS.has(node.children[idx - 1]?.node_kind ?? '');
            return (
              <Fragment key={child.node_id}>
                {showRegionless && (
                  <div
                    className="text-[10px] uppercase tracking-wide text-muted-foreground/60 pt-1.5 pb-0.5"
                    style={{ paddingLeft: (depth + 1) * 12 + 8 }}
                  >
                    Not region-specific
                  </div>
                )}
                <TreeNode
                  node={child}
                  depth={depth + 1}
                  selection={selection}
                  onSelect={onSelect}
                />
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Overview shown when a NODE (not a single category) is selected: pick a category. */
function CategoryOverview({
  node,
  onSelect,
}: {
  node: ResolvedTreeNode;
  onSelect: (sel: Selection) => void;
}) {
  if (node.category_counts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">No questions at this level.</p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Select a category to load its answers.
      </p>
      {node.category_counts.map((cat, i) => (
        <button
          key={cat.category_node_id ?? `cat-${i}`}
          type="button"
          onClick={() => onSelect({ nodeId: node.node_id, catIndex: i })}
          className="flex items-center gap-2 w-full text-left p-2.5 rounded-md border hover:bg-muted/60 transition-colors"
        >
          <Tag className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium flex-1 truncate">{cat.display_name}</span>
          <StatusDots counts={cat} />
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}

/** RIGHT pane: a node's category overview, or one selected category's lazy-loaded answers. */
function NodeAnswers({
  node,
  path,
  catIndex,
  onSelect,
}: {
  node: ResolvedTreeNode;
  path: ResolvedTreeNode[];
  catIndex: number | null;
  onSelect: (sel: Selection) => void;
}) {
  const selectedCat: CategoryCount | undefined =
    catIndex !== null ? node.category_counts[catIndex] : undefined;
  const categoryNodeId = selectedCat ? selectedCat.category_node_id : null;
  // Lazy detail read — only when a concrete (non-Uncategorized) category is selected.
  const detail = useResolvedNodeAnswers({
    nodeId: node.node_id,
    categoryNodeId,
    enabled: selectedCat !== undefined && categoryNodeId !== null,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 flex-wrap text-xs text-muted-foreground">
        {path.map((n, i) => (
          <span key={n.node_id} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3" />}
            <span className={cn(i === path.length - 1 && catIndex === null && 'text-foreground font-medium')}>
              {n.display_name}
            </span>
          </span>
        ))}
        {selectedCat && (
          <span className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{selectedCat.display_name}</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <h3 className={headingClass(0)}>
          {selectedCat ? selectedCat.display_name : node.display_name}
        </h3>
        <Badge variant="outline" className="text-xs py-0 px-1.5 font-normal">
          {selectedCat ? `in ${node.display_name}` : kindLabel(node.node_kind)}
        </Badge>
      </div>

      {!selectedCat ? (
        <CategoryOverview node={node} onSelect={onSelect} />
      ) : detail.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading answers…
        </div>
      ) : detail.isError ? (
        <p className="text-sm text-red-600 py-8 text-center">Couldn’t load answers — please retry.</p>
      ) : detail.data && detail.data.questions.length > 0 ? (
        <div className="space-y-5">
          <CategoryGroup category={detail.data} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground py-8 text-center">No questions at this level.</p>
      )}
    </div>
  );
}

/**
 * Read-only resolved-story view (Wellington #5): a master-detail browser. The
 * LEFT tree is the product spine with each level's CATEGORIES injected beneath
 * it; selecting a level (or a category within it) renders its resolved Q&As on
 * the RIGHT with inheritance-provenance badges — own (green), inherited (blue +
 * source depth), gap (red), N/A (gray). Presentational only.
 */
export function ResolvedStoryView({ story, isLoading = false, className }: ResolvedStoryViewProps) {
  const roots = useMemo(() => (story ? buildTree(story.nodes) : []), [story]);
  const byId = useMemo(() => {
    const m = new Map<number, ResolvedTreeNode>();
    const walk = (ns: ResolvedTreeNode[]) =>
      ns.forEach((n) => {
        m.set(n.node_id, n);
        walk(n.children);
      });
    walk(roots);
    return m;
  }, [roots]);

  // The firm is the persisted root but is page CONTEXT, not a tree row (SME-locked
  // shape): unwrap it so the tree starts at the Asset Class header, and surface the
  // firm name as a heading above the tree.
  const firmRoot =
    roots.length === 1 && roots[0].node_kind === 'firm' ? roots[0] : null;
  const treeRoots = firmRoot ? firmRoot.children : roots;

  const [selection, setSelection] = useState<Selection | null>(null);
  const effectiveSelection: Selection | null =
    selection ?? (treeRoots[0] ? { nodeId: treeRoots[0].node_id, catIndex: null } : null);
  const selectedNode =
    effectiveSelection !== null ? byId.get(effectiveSelection.nodeId) ?? null : null;
  const catIndex =
    effectiveSelection && selectedNode && effectiveSelection.catIndex !== null
      ? effectiveSelection.catIndex
      : null;

  const path = useMemo(() => {
    if (!selectedNode) return [];
    const chain: ResolvedTreeNode[] = [];
    let cur: ResolvedTreeNode | undefined = selectedNode;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parent_id !== null ? byId.get(cur.parent_id) : undefined;
    }
    // Firm is page context, not a breadcrumb row.
    return chain.filter((n) => n.node_kind !== 'firm');
  }, [selectedNode, byId]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Entries</span>
        {story && (
          <span className="text-sm text-muted-foreground tabular-nums">
            · {story.nodes.length.toLocaleString()} levels
          </span>
        )}
      </div>

      {CONTENT_BLOCKS_ENABLED && (
        <ContentBlockAuthoring selectedNodeId={effectiveSelection?.nodeId ?? null} />
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : treeRoots.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <GitBranch className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No resolved story yet. Map content into the product hierarchy to resolve answers.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* LEFT: spine tree with categories injected per level */}
          <Card className="lg:col-span-1 h-fit lg:sticky lg:top-4">
            <CardContent className="p-2 space-y-0.5 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {firmRoot && (
                <div className="px-2 pt-1 pb-2 mb-1 border-b">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
                    Firm
                  </div>
                  <div className="text-sm font-semibold truncate" title={firmRoot.display_name}>
                    {firmRoot.display_name}
                  </div>
                </div>
              )}
              {treeRoots.map((node) => (
                <TreeNode
                  key={node.node_id}
                  node={node}
                  depth={0}
                  selection={effectiveSelection}
                  onSelect={setSelection}
                />
              ))}
            </CardContent>
          </Card>

          {/* RIGHT: selected level's / category's answers */}
          <Card className="lg:col-span-3">
            <CardContent className="p-5">
              {selectedNode ? (
                <NodeAnswers
                  node={selectedNode}
                  path={path}
                  catIndex={catIndex}
                  onSelect={setSelection}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-12 text-center">
                  Select a level on the left to see its answers.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
